/**
 * LOGOS → KAIRA Metric Bridge
 * Translates Logos computational layers into KAIRA AIFET formulation
 * 
 * This is the bidirectional translation layer between the two systems.
 * Every Logos metric maps to a KAIRA AIFET parameter.
 * 
 * @author Dr. José Manuel Cadena
 */

import type { LogosKairaBridge } from './types';

// Levin signal key → AIFET pattern name
const LEVIN_TO_AIFET: Record<string, string> = {
  depressive:   'MOOD — DEPRESSIVE: Locked-in patterns (collapsed policy space + rigid negative priors)',
  anxiety:      'ANXIETY: Threat-scanning patterns (chronically elevated Π_sensory on threat channels)',
  spiritual:    'DISSOCIATIVE: Fragmentation patterns (spiritual disconnection = precision partitioning)',
  isolation:    'PERSONALITY: Detachment patterns (attenuated social precision)',
  burnout:      'SOMATIC: Body-prediction patterns (excessively precise illness priors)',
  dysbiosis:    'SOMATIC: Functional patterns (gut-brain axis disruption → interoceptive noise)',
  hedonicFood:  'SUBSTANCE: Hijacked-precision patterns (gustatory dopamine override)',
  entropicIntox:'SOMATIC: Full alimentary collapse — ACUTE (interoceptive channel poisoned)',
  negAttractor: 'SUICIDAL COMPUTATION: All 4 factors converging (policy collapse + prior crystallization + interoceptive blunting + self-devaluation)',
};

// Hoffman bias key → AIFET precision impact
const HOFFMAN_TO_PRECISION: Record<string, string> = {
  overconfidence:           'Π_prior: artificially RIGID through overconfidence bias (clarity without wisdom)',
  hedonicTreadmill:         'Π_policy: pleasure-seeking policies dominating (joy without gratitude = hedonic drift)',
  futureNoPresent:          'Π_self: future-projected identity — present not inhabited (mission without presence)',
  empathicFatigue:          'Π_interoceptive: HYPO from compassion depletion (giving without self-monitoring)',
  prematureEnlightenment:   'Π_self: RIGID_INFLATED — ego-signal confusion (high Λ without wisdom = inflation risk)',
  gustatoryHedonism:        'Π_interoceptive: gustatory channel ungrounded (pleasure without presence)',
  disconnectedAsceticism:   'Π_interoceptive: HYPO — body denied as spiritual channel (faith without nourishment)',
  somaticNihilism:          'Π_interoceptive: CRITICAL — all channels closing (nihilism spreads from gut upward)',
};

export function computeLogosBridge(params: {
  logosState: Record<string, number>;
  lambda: number;
  freeEnergy: number;
  viability: number;
  entropy: number;
  coherence: number;
  levinSignals: Array<{ type: string; signalKey?: string; severity: number }>;
  hoffmanBiases: Array<{ type: string; biasKey?: string }>;
  policyVerdict: string;
}): LogosKairaBridge {
  const { logosState, lambda, freeEnergy, viability, entropy, coherence, levinSignals, hoffmanBiases, policyVerdict } = params;

  const peaceVal = logosState['peace'] ?? 0.5;
  const energyVal = logosState['energy'] ?? 0.5;
  const gutRes = logosState['gut_resonance'] ?? 0.5;

  // === Π_sensory ===
  const pi_sensory_hint: LogosKairaBridge['precision_sensory_hint'] =
    entropy > 0.55
      ? (peaceVal < 0.30 ? 'hyper' : 'hypo')
      : 'flexible';

  // === Π_prior ===
  const pi_prior_hint: LogosKairaBridge['precision_prior_hint'] =
    lambda > 0.65 ? 'flexible'
    : lambda < 0.25 && freeEnergy < 0.30 ? 'rigid'
    : freeEnergy > 0.60 ? 'weak'
    : 'flexible';

  // === Π_policy ===
  const pi_policy_hint: LogosKairaBridge['precision_policy_hint'] =
    ['PAUSA', 'ESPERA'].includes(policyVerdict) ? 'narrow'
    : policyVerdict === 'ACTÚA' ? 'flexible'
    : policyVerdict === 'RECONECTA' ? 'diffuse'
    : 'flexible';

  // === Π_interoceptive ===
  const hasLevinSomatic = levinSignals.some(s =>
    ['dysbiosis', 'burnout', 'entropicIntox'].includes(s.signalKey ?? '')
  );
  const pi_interoceptive_hint: LogosKairaBridge['precision_interoceptive_hint'] =
    gutRes < 0.25 && hasLevinSomatic ? 'hyper'
    : gutRes < 0.30 && energyVal < 0.30 ? 'hypo'
    : 'flexible';

  // === Π_self ===
  const hasInflation = hoffmanBiases.some(b => b.biasKey === 'prematureEnlightenment');
  const pi_self_hint: LogosKairaBridge['precision_self_hint'] =
    coherence < 0.30 && lambda < 0.30 ? 'fragmented'
    : lambda > 0.75 && hasInflation ? 'rigid_inflated'
    : lambda < 0.25 && (logosState['meaning'] ?? 0.5) < 0.25 ? 'rigid_deflated'
    : coherence < 0.40 ? 'volatile'
    : 'flexible';

  // === Mode hint ===
  const hasDepressive = levinSignals.some(s => ['depressive', 'spiritual'].includes(s.signalKey ?? ''));
  const mode_hint: LogosKairaBridge['mode_hint'] =
    lambda < 0.30 || hasDepressive ? 'solenoidal'
    : entropy > 0.60 || coherence < 0.30 ? 'gradient'
    : 'balanced';

  // === Phase lower bound ===
  const phase_lower_bound: LogosKairaBridge['phase_lower_bound'] =
    pi_prior_hint === 'rigid' ? 1
    : pi_sensory_hint !== 'flexible' ? 2
    : 3;

  // === Risk level ===
  const hasNegAttractor = levinSignals.some(s => s.type.includes('ATRACTOR NEGATIVO'));
  const criticalSignals = levinSignals.filter(s => s.severity > 0.80).length;
  const risk_level_hint: LogosKairaBridge['risk_level_hint'] =
    hasNegAttractor ? 'acute'
    : criticalSignals >= 2 ? 'high'
    : lambda < 0.20 ? 'moderate'
    : viability < 0.30 ? 'moderate'
    : 'low';

  // === Pattern mappings ===
  const levin_to_aifet_patterns = levinSignals
    .map(s => LEVIN_TO_AIFET[s.signalKey ?? ''])
    .filter(Boolean);

  const hoffman_to_aifet_precision = hoffmanBiases
    .map(b => HOFFMAN_TO_PRECISION[b.biasKey ?? ''])
    .filter(Boolean);

  return {
    precision_sensory_hint: pi_sensory_hint,
    precision_prior_hint: pi_prior_hint,
    precision_policy_hint: pi_policy_hint,
    precision_interoceptive_hint: pi_interoceptive_hint,
    precision_self_hint: pi_self_hint,
    mode_hint,
    phase_lower_bound,
    risk_level_hint,
    levin_to_aifet_patterns,
    hoffman_to_aifet_precision,
  };
}
